/**
 * Frontend utility for checking program access permissions
 * Uses the backend API to check permissions for the current user
 */

import api from '../api/api';

/**
 * Check program access permissions for the current user
 * @param {number} fundingProgramId - The funding program ID
 * @returns {Promise<Object>} - Permission object with role and permissions
 */
export const checkProgramPermissions = async (fundingProgramId) => {
  try {
    const response = await api.get({
      path: `/v2/edoUserProgramAccesses/permissions?fundingProgramId=${fundingProgramId}`,
    });
    return response;
  } catch (error) {
    console.error('Error checking program permissions:', error);
    // Return default no-access permissions on error
    return {
      hasAccess: false,
      role: null,
      permissions: {
        canView: false,
        canInsert: false,
        canUpdate: false,
        canDelete: false,
        canManageStaff: false,
        canRunReports: false,
        canManageMaintenance: false,
        canViewMaintenance: false,
        canManageEventTypes: false,
        canViewEventTypes: false,
        canRetrievePDFs: false,
        canUpdateLenderCalcapNumber: false,
        canManageListMaintenance: false,
        canRetrieveAndSaveApplications: false,
      },
    };
  }
};

/**
 * Hook-like function to check if user can perform an action
 * @param {number} fundingProgramId - The funding program ID
 * @param {string} permission - One of: 'canView', 'canInsert', 'canUpdate', 'canDelete', 'canManageStaff', 
 *                               'canRunReports', 'canManageMaintenance', 'canViewMaintenance', 
 *                               'canManageEventTypes', 'canViewEventTypes', 'canRetrievePDFs', 
 *                               'canUpdateLenderCalcapNumber', 'canManageListMaintenance', 
 *                               'canRetrieveAndSaveApplications'
 * @returns {Promise<boolean>}
 */
export const canPerformAction = async (fundingProgramId, permission) => {
  const perms = await checkProgramPermissions(fundingProgramId);
  return perms.permissions[permission] || false;
};

/**
 * Check if user has a specific role
 * @param {number} fundingProgramId - The funding program ID
 * @param {string} role - One of: 'ADMIN', 'ANALYST', 'MANAGER', 'STAFF', 'VIEWER'
 * @returns {Promise<boolean>}
 */
export const hasRole = async (fundingProgramId, role) => {
  const perms = await checkProgramPermissions(fundingProgramId);
  return perms.role === role;
};

